import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { adminApi } from '../api/admin.js';
import { ApiError } from '../api/client.js';

export default function AdminAddEmployeePage() {
    const navigate = useNavigate();
    const today = new Date().toISOString().slice(0, 10);

    const [form, setForm] = useState({
        name: '',
        email: '',
        password: '',
        role: 'employee',
        department: '',
        date_of_joining: today,
        shift_start_time: '10:00',
        shift_end_time: '19:00',
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setFieldErrors({});
        setSubmitting(true);
        try {
            const result = await adminApi.createEmployee(form);
            navigate(`/admin/employees/${result.employee.id}`, { replace: true });
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.code === 'PASSWORD_INVALID') {
                    const failed = err.details?.rules_failed || [];
                    setFieldErrors({ password: passwordErrorMessage(failed) });
                } else if (err.code === 'EMAIL_TAKEN') {
                    setFieldErrors({ email: 'This email is already registered.' });
                } else if (err.code === 'VALIDATION_ERROR') {
                    setError(err.message);
                } else {
                    setError(err.message);
                }
            } else {
                setError('Could not create employee. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Breadcrumb>
                <Link to="/admin/employees" style={{ color: 'var(--color-text-tertiary)', textDecoration: 'none' }}>
                    Employees
                </Link>
                <Sep />
                <span>Add new</span>
            </Breadcrumb>

            <h1 style={{ fontSize: 20, fontWeight: 500, marginBottom: 18 }}>Add a new employee</h1>

            <form
                onSubmit={onSubmit}
                className="surface-card flex flex-col"
                style={{ padding: 24, maxWidth: 640, gap: 14 }}
            >
                <Field label="Full name" required>
                    <input type="text" required value={form.name} onChange={onChange('name')} className="input" />
                </Field>

                <Field label="Email" required error={fieldErrors.email}>
                    <input type="email" required value={form.email} onChange={onChange('email')} className="input" />
                </Field>

                <Field
                    label="Initial password"
                    required
                    error={fieldErrors.password}
                    helper="At least 10 characters and 1 special character. The employee will be required to change it on first login."
                >
                    <input type="text" required value={form.password} onChange={onChange('password')} className="input" />
                </Field>

                <div className="grid" style={{ gap: 14, gridTemplateColumns: '1fr 1fr' }}>
                    <Field label="Role">
                        <select value={form.role} onChange={onChange('role')} className="select">
                            <option value="employee">Employee</option>
                            <option value="manager">Manager</option>
                        </select>
                    </Field>
                    <Field label="Department" required>
                        <input
                            type="text"
                            required
                            value={form.department}
                            onChange={onChange('department')}
                            className="input"
                            placeholder="e.g. Backend, Frontend, QA"
                        />
                    </Field>
                </div>

                <Field label="Date of joining" required>
                    <input
                        type="date"
                        required
                        value={form.date_of_joining}
                        onChange={onChange('date_of_joining')}
                        className="input"
                    />
                </Field>

                <div className="grid" style={{ gap: 14, gridTemplateColumns: '1fr 1fr' }}>
                    <Field label="Shift start" required>
                        <input
                            type="time"
                            required
                            value={form.shift_start_time}
                            onChange={onChange('shift_start_time')}
                            className="input"
                        />
                    </Field>
                    <Field label="Shift end" required>
                        <input
                            type="time"
                            required
                            value={form.shift_end_time}
                            onChange={onChange('shift_end_time')}
                            className="input"
                        />
                    </Field>
                </div>

                <p
                    style={{
                        fontSize: 12,
                        color: 'var(--color-text-secondary)',
                        background: 'var(--color-bg-secondary)',
                        borderRadius: 'var(--border-radius-md)',
                        padding: 10,
                        lineHeight: 1.5,
                    }}
                >
                    The shift takes effect on the date of joining. You can change it later from the employee’s profile.
                </p>

                {error && (
                    <div
                        style={{
                            background: 'var(--color-pill-rejected-bg)',
                            border: '0.5px solid var(--color-danger-border)',
                            color: 'var(--color-danger)',
                            borderRadius: 'var(--border-radius-md)',
                            padding: '8px 12px',
                            fontSize: 13,
                        }}
                    >
                        {error}
                    </div>
                )}

                <div
                    className="flex justify-end"
                    style={{
                        gap: 8,
                        paddingTop: 8,
                        borderTop: '0.5px solid var(--color-border-tertiary)',
                    }}
                >
                    <Link to="/admin/employees" className="btn btn-ghost">
                        Cancel
                    </Link>
                    <button type="submit" disabled={submitting} className="btn btn-primary">
                        {submitting ? 'Creating…' : 'Create employee'}
                    </button>
                </div>
            </form>
        </>
    );
}

function Field({ label, required, error, helper, children }) {
    return (
        <div>
            <label
                style={{
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--color-text-secondary)',
                    marginBottom: 6,
                }}
            >
                {label}
                {required && <span style={{ color: 'var(--color-danger)' }}> *</span>}
            </label>
            {children}
            {helper && !error && (
                <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4, lineHeight: 1.5 }}>
                    {helper}
                </p>
            )}
            {error && (
                <p style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 4 }}>{error}</p>
            )}
        </div>
    );
}

function Breadcrumb({ children }) {
    return (
        <div className="flex items-center" style={{ gap: 8, fontSize: 13, marginBottom: 14 }}>
            {children}
        </div>
    );
}

function Sep() {
    return <span style={{ color: 'var(--color-text-tertiary)' }}>/</span>;
}

function passwordErrorMessage(failed) {
    const parts = [];
    if (failed.includes('min_length')) parts.push('must be at least 10 characters');
    if (failed.includes('special_char')) parts.push('must contain at least 1 special character');
    if (failed.includes('blocklisted')) parts.push('is too common — pick something less guessable');
    if (parts.length === 0) return 'does not meet password requirements';
    return 'Password ' + parts.join(', ') + '.';
}
